import * as React from 'react';
import { createPortal } from 'react-dom';

import { HelpRule, HelpRuleOverrides } from './interfaces';
import { LONG_TIME_LABEL, LOW_SCORE_LABEL } from './mayNeedHelp';

import useModalAccessibility from '../../../Shared/hooks/useModalAccessibility';
import { DropdownInput, pluralize } from '../../../Shared/index';

// One Low Score and one Long Time on Question dropdown per group of activities
interface Choices { min: number, max: number, step: number }

interface Group {
  key: string,
  title: string,
  description: string,
  toolKeys: string[],
  lowScore: Choices,
  longTime: Choices,
}

const range = (min: number, max: number, step: number): Choices => ({ min, max, step });

export const GROUPS: Group[] = [
  {
    key: 'language',
    title: 'Language Skills Activities',
    description: 'This includes Connect, Grammar, and Proofreader activities.',
    toolKeys: ['connect', 'sentence', 'passage'],
    lowScore: range(1, 5, 1),
    longTime: range(1, 8, 0.5),
  },
  {
    key: 'evidence',
    title: 'Reading For Evidence',
    description: 'These activities have less questions but typically take longer for each question.',
    toolKeys: ['evidence'],
    lowScore: range(1, 2, 1),
    longTime: range(3, 10, 0.5),
  },
];

interface GroupSettings { minFailed: number, minutes: number }

const nearestChoice = (value: number, { min, max, step }: Choices) => Math.min(Math.max(Math.round(value / step) * step, min), max);

const settingsFor = (group: Group, rules: HelpRule[]): GroupSettings|null => {
  const rule = rules.find(candidate => candidate.toolKey === group.toolKeys[0]);
  if (!rule || rule.unflaggedReason) { return null; }

  return { minFailed: nearestChoice(rule.minFailed, group.lowScore), minutes: nearestChoice(rule.questionSeconds / 60, group.longTime) };
};

const describeIncorrect = (count: number) => `${count} incorrect`;
const describeMinutes = (minutes: number) => `${minutes} ${pluralize(minutes, 'minute', 'minutes')}`;

interface Option { label: string, value: number }

const optionsFor = ({ min, max, step }: Choices, describe: (value: number) => string): Option[] => (
  Array.from({ length: Math.round((max - min) / step) + 1 }, (_, i) => {
    const value = min + i * step;
    return { label: describe(value), value };
  })
);

interface SettingProps {
  id: string,
  label: string,
  value: number,
  defaultValue: number|null,
  describe: (value: number) => string,
  choices: Choices,
  onChange: (value: number) => void,
}

const Setting = ({ id, label, value, defaultValue, describe, choices, onChange, }: SettingProps) => {
  const options = React.useMemo(() => optionsFor(choices, describe), [choices, describe]);
  const handleChange = React.useCallback((option: Option) => onChange(option.value), [onChange]);

  return (
    <DropdownInput
      className="may-need-help-setting small bordered"
      handleChange={handleChange}
      helperText={defaultValue === null ? undefined : `Default: ${describe(defaultValue)}`}
      id={id}
      label={label}
      options={options}
      // Opens upward when the modal has no room below
      selectProps={{ menuPlacement: 'auto' }}
      value={options.find(option => option.value === value)}
    />
  );
};

interface GroupSectionProps {
  group: Group,
  settings: GroupSettings,
  defaults: GroupSettings|null,
  onChange: (groupKey: string, change: Partial<GroupSettings>) => void,
}

const GroupSection = ({ group, settings, defaults, onChange, }: GroupSectionProps) => {
  const handleLowScore = React.useCallback((minFailed: number) => onChange(group.key, { minFailed }), [group.key, onChange]);
  const handleLongTime = React.useCallback((minutes: number) => onChange(group.key, { minutes }), [group.key, onChange]);

  return (
    <section aria-labelledby={`may-need-help-settings-${group.key}`} className="may-need-help-settings-group">
      <h3 id={`may-need-help-settings-${group.key}`}>{group.title}</h3>
      <p>{group.description}</p>
      <div className="may-need-help-settings-inputs">
        <Setting
          choices={group.lowScore}
          defaultValue={defaults && defaults.minFailed}
          describe={describeIncorrect}
          id={`may-need-help-${group.key}-low-score`}
          label={LOW_SCORE_LABEL}
          onChange={handleLowScore}
          value={settings.minFailed}
        />
        <Setting
          choices={group.longTime}
          defaultValue={defaults && defaults.minutes}
          describe={describeMinutes}
          id={`may-need-help-${group.key}-long-time`}
          label={LONG_TIME_LABEL}
          onChange={handleLongTime}
          value={settings.minutes}
        />
      </div>
    </section>
  );
};

interface MayNeedHelpSettingsModalProps {
  rules: HelpRule[],
  defaultRules: HelpRule[],
  onSave: (toolKey: string, override: HelpRuleOverrides[string]) => void,
  closeModal: () => void,
}

const MayNeedHelpSettingsModal = ({ rules, defaultRules, onSave, closeModal, }: MayNeedHelpSettingsModalProps) => {
  const { modalRef } = useModalAccessibility(closeModal);
  const initial = React.useMemo(() => Object.fromEntries(GROUPS.map(group => [group.key, settingsFor(group, rules)])), [rules]);
  const [settings, setSettings] = React.useState<Record<string, GroupSettings|null>>(initial);

  const handleChange = React.useCallback((groupKey: string, change: Partial<GroupSettings>) => {
    setSettings(current => ({ ...current, [groupKey]: { ...current[groupKey], ...change } }));
  }, []);

  const defaults = React.useMemo(() => Object.fromEntries(GROUPS.map(group => [group.key, settingsFor(group, defaultRules)])), [defaultRules]);
  const differsFrom = (other: Record<string, GroupSettings|null>) => GROUPS.some(group => JSON.stringify(settings[group.key]) !== JSON.stringify(other[group.key]));
  const changed = differsFrom(initial);
  const atDefaults = !differsFrom(defaults);

  // Fills in the defaults; like any other change, Save applies them
  const handleRestoreDefaults = () => {
    setSettings(current => Object.fromEntries(GROUPS.map(group => [group.key, current[group.key] && (defaults[group.key] || current[group.key])])));
  };

  const handleSave = () => {
    GROUPS.forEach((group) => {
      const groupSettings = settings[group.key];
      if (!groupSettings) { return; }

      group.toolKeys.forEach(toolKey => onSave(toolKey, {
        minFailed: groupSettings.minFailed,
        questionSeconds: Math.round(groupSettings.minutes * 60),
      }));
    });
    closeModal();
  };

  // On <body>, so nothing in the page layout can stack above it and catch the
  // clicks that the accessibility hook treats as clicking outside
  return createPortal(
    <div className="modal-container may-need-help-settings-modal-container">
      <div className="modal-background" />
      <div
        aria-labelledby="may-need-help-settings-title"
        aria-modal="true"
        className="may-need-help-settings-modal quill-modal modal-body"
        ref={modalRef}
        role="dialog"
        tabIndex={-1}
      >
        <div className="title">
          <h2 id="may-need-help-settings-title">Set May Need Help Limits</h2>
        </div>
        <p>You can adjust what should flag a student as &lsquo;May Need Help&rsquo;. Note, Diagnostic activities do not trigger this warning.</p>
        {GROUPS.map(group => settings[group.key] && (
          <GroupSection
            defaults={defaults[group.key]}
            group={group}
            key={group.key}
            onChange={handleChange}
            settings={settings[group.key]}
          />
        ))}
        <div className="form-buttons">
          <button className="quill-button outlined large grey focus-on-light" disabled={atDefaults} onClick={handleRestoreDefaults} type="button">Restore Defaults</button>
          <div className="form-buttons-end">
            <button className="quill-button outlined large grey focus-on-light" onClick={closeModal} type="button">Cancel</button>
            <button className="quill-button contained large green focus-on-light" disabled={!changed} onClick={handleSave} type="button">Save settings</button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default MayNeedHelpSettingsModal;
